import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { SlotId } from "@skilitics/nautilus-core-designer-data";
import { getNodeSlotIdSelector } from "@skilitics/nautilus-core-designer-data";

import { getDesignerNodeByIdSelector } from "../state/contextualSelectors/index.ts";
import { useExportNodeDesign } from "./useExportNodeDesign.ts";

type Action = "cut" | "copy";

type CopyOrCutSpec = { slotId: SlotId } | { slotResidentNodeId: number };

export const useSlotCopyClipboard = () => {
  const getSlotIdById = useSelector(getNodeSlotIdSelector);
  const getById = useSelector(getDesignerNodeByIdSelector);
  const exportNodeDesign = useExportNodeDesign();

  const copyOrCut = useMemo(
    () => (action: Action, spec: CopyOrCutSpec) => {
      const slotId = "slotId" in spec ? spec.slotId : getSlotIdById(spec.slotResidentNodeId);
      if (!slotId) {
        throw new Error(`Slot not found`);
      }
      const residentEntry = getById(slotId.parentId).childNodes[slotId.name];
      if (slotId.index === undefined && Array.isArray(residentEntry)) {
        throw new Error(`Slot reference is to a multi-value but no index specified`);
      }
      const node = Array.isArray(residentEntry) ? residentEntry[slotId.index ?? 0] : residentEntry;
      if (!node) {
        throw new Error(
          `Node not found to cut at ${slotId.parentId}:${slotId.name}${
            slotId.index !== undefined ? `[${slotId.index}]` : ""
          }`,
        );
      }
      const content = exportNodeDesign(node.id, { slotId, action });
      navigator.clipboard
        .writeText(JSON.stringify(content))
        .then(() => {
          // Dispatch a clipboard event so we can listen for it in other components
          window.dispatchEvent(new ClipboardEvent(action));
        })
        .catch((error) => {
          throw new Error(`You need to grant your browser permission to access the clipboard. ${error.message}`);
        });
    },
    [exportNodeDesign, getById, getSlotIdById],
  );

  return useMemo(
    () => ({
      cut: (spec: CopyOrCutSpec) => copyOrCut("cut", spec),
      copy: (spec: CopyOrCutSpec) => copyOrCut("copy", spec),
    }),
    [copyOrCut],
  );
};
